import { useState, useEffect, useContext, useRef } from "react";
import {
  Grid,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import RecipeList from "../Recipes/RecipeList";
import SelectedRecipeDetail from "../Recipes/SelectedRecipeDetail";
import supabase from "../supabaseClient";
import { ShoppingList } from "./ShoppingList";
import { CustomMarkdown } from "./CustomMarkdown";
import { useTheme } from "@mui/material/styles";
import UserContext from "../contexts/userContext";

function FavoriteRecipes({ listId, listName }) {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [shoppingListLoading, setShoppingListLoading] = useState(false);
  const [markdownContent, setMarkdownContent] = useState("");
  const [isListExist, setIsListExist] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Desktop-specific state
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [activeTab, setActiveTab] = useState("recipes"); // "recipes" or "shopping"

  const streamAbortRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchFavoriteRecipes = async () => {
      try {
        const { data: recipesData, error: recipesError } = await supabase
          .from("lists_recipes")
          .select(
            `
            list_recipe_id,
            recipes (
              *,
              books ( name, author )
            )
          `
          )
          .eq("list_id", listId);

        if (recipesError || !recipesData) {
          throw new Error("Could not fetch favorite recipes.");
        }

        // Formatting the data as required
        const formattedData = recipesData.map((item) => ({
          list_recipe_id: item.list_recipe_id,
          id: item.recipes.id,
          title: item.recipes.title,
          description: item.recipes.description,
          ingredients: item.recipes.ingredients,
          instructions: item.recipes.instructions,
          yield: item.recipes.yield,
          time: item.recipes.time,
          inactive_time: item.recipes.inactive_time,
          path: item.recipes.path,
          image_url: item.recipes.image_url,
          books: item.recipes.books ? item.recipes.books : null,
        }));

        setFavoriteRecipes(formattedData);
        setLoading(false);
      } catch (error) {
        setErrorMessage(error.message);
        setLoading(false);
      }
    };

    fetchFavoriteRecipes();

    // Supabase Realtime Subscription for lists_recipes
    const listsRecipesSubscription = supabase
      .channel(`public:lists_recipes_list_id=eq.${listId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "lists_recipes",
          filter: `list_id=eq.${listId}`,
        },
        (payload) => {
          console.log("Realtime update on lists_recipes:", payload);
          fetchFavoriteRecipes();
        }
      )
      .subscribe();

    // Clean up subscription on unmount
    return () => {
      supabase.removeChannel(listsRecipesSubscription);
    };
  }, [user, listId]);

  useEffect(() => {
    const fetchShoppingList = async () => {
      try {
        const { data: listData, error: listError } = await supabase
          .from("lists_shopping_list")
          .select("ingredient_json, markdown_content")
          .eq("list_id", listId);

        if (listError) {
          throw new Error("Could not fetch shopping list content.");
        }

        if (listData && listData.length > 0) {
          setIsListExist(true);
          setMarkdownContent(listData[0].markdown_content || "");
        } else {
          setIsListExist(false);
          setMarkdownContent("");
        }
      } catch (error) {
        console.error("Error fetching shopping list content:", error);
      }
    };

    if (listId) {
      fetchShoppingList();

      // Supabase Realtime Subscription for lists_shopping_list
      const listsShoppingListSubscription = supabase
        .channel(`public:lists_shopping_list_list_id=eq.${listId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "lists_shopping_list",
            filter: `list_id=eq.${listId}`,
          },
          (payload) => {
            console.log("Realtime update on lists_shopping_list:", payload);
            if (payload.eventType === "DELETE") {
              setIsListExist(false);
              setMarkdownContent("");
            } else if (payload.new) {
              setIsListExist(true);
              setMarkdownContent(payload.new.markdown_content || "");
            }
          }
        )
        .subscribe();

      // Clean up subscription on unmount
      return () => {
        supabase.removeChannel(listsShoppingListSubscription);
      };
    }
  }, [listId]);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const parseSSEAndAppend = (sseChunk, onDataLine) => {
    // SSE frames are separated by a blank line.
    // We only use "data:" lines for this endpoint.
    const frames = sseChunk.split(/\r?\n\r?\n/);
    for (const frame of frames) {
      const lines = frame.split(/\r?\n/);
      for (const line of lines) {
        if (line.startsWith(":")) continue; // keep-alive comment
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        onDataLine(data);
      }
    }
  };

  const generateShoppingList = async () => {
    setMarkdownContent("");
    setShoppingListLoading(true);
    setIsGenerating(true);

    // Cancel any in-flight stream
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
    }
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const allIngredients = favoriteRecipes.flatMap((recipe) =>
      recipe.ingredients.map((ing) => ({
        name: ing.name,
        quantity: ing.quantity,
      }))
    );

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(`${SERVER_URL}/api/lists/create-list`, {
        method: "POST",
        body: JSON.stringify({ ingredients: allIngredients, list_id: listId }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(`Failed to start list generation (${response.status}) ${body}`.trim());
      }

      const { requestId } = await response.json();

      const streamResponse = await fetch(
        `${SERVER_URL}/api/lists/create-list/${requestId}`,
        {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${accessToken}`,
          },
          signal: abortController.signal,
        }
      );

      if (!streamResponse.ok || !streamResponse.body) {
        const body = await streamResponse.text().catch(() => "");
        throw new Error(`Failed to open stream (${streamResponse.status}) ${body}`.trim());
      }

      const reader = streamResponse.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Only process complete frames (ending in a blank line). Keep remainder in buffer.
        const parts = buffer.split(/\r?\n\r?\n/);
        buffer = parts.pop() || "";

        for (const part of parts) {
          parseSSEAndAppend(part, (dataLine) => {
            const content = JSON.parse(dataLine);
            setMarkdownContent((prevContent) => prevContent + content);
          });
        }
      }

      // Process any remaining buffered content (best-effort)
      if (buffer.trim()) {
        parseSSEAndAppend(buffer, (dataLine) => {
          const content = JSON.parse(dataLine);
          setMarkdownContent((prevContent) => prevContent + content);
        });
      }

      // Stream ended; server should have persisted ingredient_json.
      setIsListExist(true);
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      console.error("Failed to send ingredients:", error);
    } finally {
      setShoppingListLoading(false);
      setIsGenerating(false);
      streamAbortRef.current = null;
    }
  };

  // Clean up stream when component unmounts
  useEffect(() => {
    return () => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
      }
    };
  }, []);

  // Handle recipe click for desktop preview
  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
  };

  // Auto-select first recipe on desktop when recipes load
  useEffect(() => {
    if (!isMobile && favoriteRecipes.length > 0 && !selectedRecipe) {
      setSelectedRecipe(favoriteRecipes[0]);
    }
  }, [isMobile, favoriteRecipes, selectedRecipe]);

  const isDark = theme.palette.mode === "dark";
  const accent = {
    primary: isDark ? "#BB86FC" : "#6366f1",
  };

  // Shopping list content component
  const shoppingListContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
      {/* Show generate button only when no list exists */}
      {!isListExist && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 5,
            px: 3,
            gap: 2.5,
            borderRadius: '16px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: `linear-gradient(135deg, ${accent.primary}20, ${accent.primary}10)`,
              border: `1px solid ${accent.primary}30`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '1.5rem' }}>🛒</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "0.9rem",
                color: theme.palette.text.primary,
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {favoriteRecipes.length === 0
                ? "no recipes yet"
                : "ready to shop?"}
            </Typography>
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "0.8rem",
                color: theme.palette.text.secondary,
                lineHeight: 1.5,
              }}
            >
              {favoriteRecipes.length === 0
                ? "add recipes to generate a shopping list"
                : "combine all ingredients into one list"}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={generateShoppingList}
            disabled={shoppingListLoading || isGenerating || favoriteRecipes.length === 0}
            sx={{ 
              fontFamily: theme.typography.fontFamily, 
              fontSize: "0.85rem",
              py: 1,
              px: 2.5,
              backgroundColor: accent.primary,
              color: isDark ? '#121212' : '#fff',
              borderRadius: "10px",
              textTransform: "lowercase",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: accent.primary,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px -4px ${accent.primary}50`,
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&:disabled": {
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                color: theme.palette.text.disabled,
              },
            }}
          >
            {isGenerating ? "generating..." : "generate list"}
          </Button>
        </Box>
      )}

      {/* Loading state with streaming markdown */}
      {shoppingListLoading && (
        <CustomMarkdown
          content={markdownContent}
          setMarkdownContent={setMarkdownContent}
          disabled={shoppingListLoading}
        />
      )}

      {/* Existing shopping list */}
      {isListExist && !shoppingListLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <ShoppingList 
            listId={listId} 
            onRegenerate={generateShoppingList} 
            isRegenerating={isGenerating} 
          />
        </Box>
      )}
    </Box>
  );

  // Mobile layout - with tabs like desktop
  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 80px)" }}>
        {/* Header */}
        <Box sx={{ pt: 2, pb: 1, flexShrink: 0 }}>
          <Typography
            sx={{ 
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "1.1rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
              letterSpacing: "-0.02em",
            }}
          >
            {listName}
          </Typography>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            mb: 1.5,
            flexShrink: 0,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": {
                backgroundColor: accent.primary,
                height: "2px",
              },
              "& .MuiTab-root": {
                minHeight: 0,
                padding: "8px 16px",
                paddingLeft: 0,
                marginRight: "16px",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.8rem",
                fontWeight: 500,
                textTransform: "lowercase",
                letterSpacing: "0.01em",
                color: theme.palette.text.secondary,
                transition: "color 0.2s ease",
                "&.Mui-selected": {
                  color: accent.primary,
                  fontWeight: 600,
                },
                "&:hover": {
                  color: theme.palette.text.primary,
                },
              },
            }}
          >
            <Tab 
              label={`recipes${favoriteRecipes.length > 0 ? ` (${favoriteRecipes.length})` : ""}`} 
              value="recipes" 
            />
            <Tab 
              label={`shopping${isListExist ? " ✓" : ""}`} 
              value="shopping" 
            />
          </Tabs>
        </Box>

        {/* Tab content */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {activeTab === "recipes" ? (
            // Recipes tab
            <>
              {loading ? (
                <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: "0.85rem" }}>
                  loading...
                </Typography>
              ) : errorMessage ? (
                <Typography color="error">{errorMessage}</Typography>
              ) : favoriteRecipes.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 5,
                    px: 3,
                    gap: 2,
                    borderRadius: '16px',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '14px',
                      background: `linear-gradient(135deg, ${accent.primary}20, ${accent.primary}10)`,
                      border: `1px solid ${accent.primary}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '1.5rem' }}>📖</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "0.9rem",
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      no recipes yet
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "0.8rem",
                        color: theme.palette.text.secondary,
                        lineHeight: 1.5,
                      }}
                    >
                      add recipes from search to get started
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <RecipeList
                  loading={loading}
                  recipes={favoriteRecipes}
                  showResultsText={false}
                  totalCount={favoriteRecipes.length}
                  isFavorited={true}
                />
              )}
            </>
          ) : (
            // Shopping tab
            shoppingListContent
          )}
        </Box>
      </Box>
    );
  }

  // Desktop layout - split pane with tabs in left column only
  return (
    <Box sx={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Header - list name */}
      <Typography
        sx={{ 
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "1.1rem",
          fontWeight: 600,
          color: theme.palette.text.primary,
          letterSpacing: "-0.02em",
          mb: 1.5,
        }}
      >
        {listName}
      </Typography>
      
      {/* Two-column layout: Left (tabs + content) | Right (preview panel) */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Column - Tabs and switchable content */}
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Tabs - only affect left column */}
          <Box
            sx={{
              borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                minHeight: 0,
                "& .MuiTabs-indicator": {
                  backgroundColor: accent.primary,
                  height: "2px",
                },
                "& .MuiTab-root": {
                  minHeight: 0,
                  padding: "8px 16px",
                  paddingLeft: 0,
                  marginRight: "16px",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  textTransform: "lowercase",
                  letterSpacing: "0.01em",
                  color: theme.palette.text.secondary,
                  transition: "color 0.2s ease",
                  "&.Mui-selected": {
                    color: accent.primary,
                    fontWeight: 600,
                  },
                  "&:hover": {
                    color: theme.palette.text.primary,
                  },
                },
              }}
            >
              <Tab 
                label={`recipes${favoriteRecipes.length > 0 ? ` (${favoriteRecipes.length})` : ""}`} 
                value="recipes" 
              />
              <Tab 
                label={`shopping${isListExist ? " ✓" : ""}`} 
                value="shopping" 
              />
            </Tabs>
          </Box>
          
          {/* Left column content - switches based on tab */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              paddingTop: "4px",
              paddingRight: 1,
              // Recipes tab scrolls here, shopping list handles its own scroll
              overflowY: activeTab === "recipes" ? "auto" : "hidden",
            }}
          >
            {activeTab === "recipes" ? (
              // Recipes list - scrollable
              <Box sx={{ flex: 1 }}>
                {loading ? (
                  <Typography>loading...</Typography>
                ) : errorMessage ? (
                  <Typography color="error">{errorMessage}</Typography>
                ) : favoriteRecipes.length === 0 ? (
                  <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: "0.85rem", color: theme.palette.text.secondary }}>
                    no recipes saved.
                  </Typography>
                ) : (
                  <RecipeList
                    loading={loading}
                    recipes={favoriteRecipes}
                    showResultsText={false}
                    totalCount={favoriteRecipes.length}
                    isFavorited={true}
                    onRecipeClick={handleRecipeClick}
                    disableExpansion={true}
                    selectedRecipe={selectedRecipe}
                  />
                )}
              </Box>
            ) : (
              // Shopping list content - flex container, handles own scrolling
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {shoppingListContent}
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Column - Recipe Preview (always visible) */}
        <Grid
          item
          xs={12}
          md={7}
          lg={8}
          sx={{ height: "100%", overflowY: "hidden" }}
        >
          {selectedRecipe ? (
            <SelectedRecipeDetail
              recipe={selectedRecipe}
              onRecipeUpdate={(updatedRecipe) => {
                setSelectedRecipe(updatedRecipe);
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: "0.85rem",
                color: theme.palette.text.secondary,
                opacity: 0.7,
                letterSpacing: "-0.01em",
              }}
            >
              {favoriteRecipes.length > 0 
                ? "select a recipe to preview"
                : "no recipes in this list"}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default FavoriteRecipes;